import React, { useMemo } from "react";
import MaterialReactTable from "material-react-table";
import { ThemeProvider, createTheme, darken } from "@mui/material";
import { Box, Flex, Loader, Text, useMantineTheme } from "@mantine/core";
import moment from "moment";
import { filterAttendees } from "utils/insights";
import { formatNumber } from "utils/common";
import { truncate } from "lodash";
import Tooltip from "components/Tooltip/Tooltip";
// import { ExportToCsv } from "export-to-csv";

const MeetingsTableCard = ({ data, isLoading, isError }) => {
  const defaultMaterialTheme = createTheme();
  const theme = useMantineTheme();
  const columns = useMemo(
    () =>
      data
        ? [
            {
              accessorKey: "summary",
              header: "Title",
              size: 280,
            },
            {
              accessorFn: (row) =>
                formatNumber(row?.costInfo?.amount, 0) || "not available",
              header: "Cost",
              Cell: ({ cell, row }) => {
                const data = row?.original;
                return (
                  <Tooltip
                    label={data?.costInfo?.toolTipMsg}
                    disabled={!data?.costInfo?.estimated}
                  >
                    <Text size="sm">
                      {data?.costInfo?.estimated
                        ? `${data?.costInfo?.dollarSign}`
                        : `$${cell.getValue()}`}
                    </Text>
                  </Tooltip>
                );
              },
              size: 120,
            },
            {
              accessorFn: (row) =>
                row?.ratings?.length > 0
                  ? (
                      row?.ratings?.reduce(
                        (accumulator, currentValue) =>
                          accumulator + currentValue,
                        0
                      ) / row?.ratings?.length
                    ).toFixed(1)
                  : "not available",
              header: "Average rating",
              size: 150,
            },
            {
              accessorFn: (row) =>
                parseFloat(
                  moment
                    .duration(
                      moment(row?.end?.dateTime).diff(
                        moment(row?.start?.dateTime)
                      )
                    )
                    .asHours()
                ).toFixed(1),
              Cell: ({ cell }) => `${cell.getValue()} hrs`,
              header: "Duration (hrs)",
              size: 120,
            },
            {
              accessorKey: "organizer.email",
              header: "Organizer",
              size: 140,
            },
            {
              header: "Strategic goal",
              accessorFn: (row) => row?.strategicGoal?.goal || "",
              Cell: ({ cell, row }) => {
                const goalObj = row?.original?.strategicGoal;
                return (
                  <Flex gap={".5rem"} align={"center"}>
                    <Box
                      sx={{
                        width: "10px",
                        height: "10px",
                        backgroundColor: theme.colors[goalObj?.color]?.[5],
                        borderRadius: "50%",
                      }}
                    />
                    <Text title={cell.getValue()}>
                      {truncate(cell.getValue(), { length: 20 })}
                    </Text>
                  </Flex>
                );
              },
              size: 180,
            },
            {
              accessorFn: (row) => row?.attendees?.length || 0,
              header: "Attendees",
              size: 140,
            },
            {
              accessorFn: (row) =>
                moment(row?.start?.dateTime).format("DD MMM YYYY"),
              header: "Date",
              sortingFn: "datetime",
              size: 140,
            },
            {
              accessorFn: (row) =>
                moment(row?.start?.dateTime).format("hh:mma"),
              header: "Started at",
              sortingFn: "datetime",
              size: 100,
            },
            {
              accessorFn: (row) => moment(row?.end?.dateTime).format("hh:mma"),
              header: "Ended at",
              sortingFn: "datetime",
              size: 100,
            },
          ]
        : [],
    [data, theme]
  );

  if (isLoading) {
    return (
      <div className="card">
        <div className="loader">
          <Loader />
          <span>Fetching meetings</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card">
        <div className="loader">
          <span>Error while fetching meetings</span>
        </div>
      </div>
    );
  }

  if (!isLoading)
    return (
      <div className="card" style={{ flex: 2, overflow: "auto" }}>
        <ThemeProvider theme={defaultMaterialTheme}>
          <MaterialReactTable
            style={{ height: "10rem" }}
            columns={columns}
            data={data ? data : []}
            enableGrouping
            enableStickyHeader
            enableColumnOrdering
            enableFullScreenToggle={true}
            enableDensityToggle={false}
            initialState={{
              density: "compact",
              expanded: false, //expand all groups by default
              pagination: { pageIndex: 0, pageSize: 100 },
              // sorting: [{ id: "state", desc: false }] //sort by state by default
            }}
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                borderRadius: "0",
                border: "none",
                width: "100%",
              },
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "25rem",
                overflow: "auto",
                width: "100%",
              },
            }}
            muiTableProps={{
              sx: {
                tableLayout: "fixed",
              },
            }}
            muiTableBodyProps={{
              sx: (theme) => ({
                "& tr:nth-of-type(odd)": {
                  backgroundColor: darken(
                    theme.palette.background.default,
                    0.02
                  ),
                },
              }),
            }}
          />
        </ThemeProvider>
      </div>
    );
};

export default MeetingsTableCard;
